import { clockOf, duration, planBlocks, timedOn } from '@dovefiniscono/core/calendar/day';
import { type Intent, parseCommand, searchKey } from '@dovefiniscono/core/command/parse';
import {
  clockNow,
  formatShortDay,
  joinInstant,
  today as todayISO,
  toISODate,
} from '@dovefiniscono/core/lib/dates';
import { formatCents } from '@dovefiniscono/core/lib/money';
import { guessCategory } from '@dovefiniscono/core/money/finance';
import { accountFor, methodLabel } from '@dovefiniscono/core/money/methods';
import { useMoney } from '@dovefiniscono/core/money/useMoney';
import { isLive } from '@dovefiniscono/core/tasks/board';
import { PRIORITY_LABELS } from '@dovefiniscono/core/tasks/logic';
import { useNavigate } from '@tanstack/react-router';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { cx, nextPosition } from '../../lib/cx';
import { useCollection, useStore } from '../../lib/data';
import { Dialog } from '../../ui/Dialog';
import { Kbd } from '../../ui/Page';
import styles from './Command.module.css';

const KIND_LABEL: Record<Intent['kind'], string> = {
  transaction: 'Movimento',
  event: 'Evento',
  block: 'Blocco di tempo',
  task: 'Attività',
};

type Result = { id: string; kind: string; title: string; meta: string; go: () => void };

/** Opens with Ctrl+K (⌘K on a Mac): one line to add anything, or to find it. */
export function CommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((value) => !value);
      }
    };
    window.addEventListener('keydown', onKey);
    const onOpen = () => setOpen(true);
    window.addEventListener('dovefiniscono:command', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('dovefiniscono:command', onOpen);
    };
  }, []);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} title="Aggiungi o cerca" width={620}>
      {open ? <Palette onDone={() => setOpen(false)} /> : null}
    </Dialog>
  );
}

/** Opens the palette from anywhere (the sidebar button). */
export const openCommand = () => window.dispatchEvent(new Event('dovefiniscono:command'));

function Palette({ onDone }: { onDone: () => void }) {
  const store = useStore();
  const navigate = useNavigate();
  const money = useMoney();
  const allTasks = useCollection('tasks');
  const events = useCollection('events');
  const tasks = useMemo(() => allTasks.filter(isLive), [allTasks]);
  const [text, setText] = useState('');
  const [reading, setReading] = useState(0);
  const [selected, setSelected] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const today = todayISO();

  const intents = useMemo(() => parseCommand(text, today), [text, today]);
  const intent = intents.length > 0 ? intents[reading % intents.length] : undefined;

  // Where a block of time would go today: the next free slot from the next quarter of an hour.
  const blockStart = (minutes: number) => {
    const now = new Date();
    const from = Math.ceil((now.getHours() * 60 + now.getMinutes()) / 15) * 15;
    const busy = timedOn(events, today);
    return planBlocks(busy, 1, minutes, from, 0, 0, 24 * 60)[0] ?? null;
  };

  const account = (method: 'cash' | 'card' | 'transfer' | null) =>
    (method ? accountFor(method, undefined, money.ownAccounts) : null) ??
    money.ownAccounts.find((item) => item.kind !== 'cash')?.id ??
    money.ownAccounts[0]?.id ??
    null;

  const describe = (item: Intent): string => {
    switch (item.kind) {
      case 'transaction': {
        const pieces = [
          formatCents(item.amount, { signed: true }),
          item.note || 'senza nota',
          item.method ? methodLabel.get(item.method)?.toLowerCase() : null,
          item.day === today ? 'oggi' : formatShortDay(item.day),
        ];
        return pieces.filter(Boolean).join(' · ');
      }
      case 'event':
        return `${item.title} · ${item.day === today ? 'oggi' : formatShortDay(item.day)} alle ${item.time}`;
      case 'block': {
        const start = blockStart(item.minutes);
        return `${item.title} · ${duration(item.minutes)}${
          start === null ? ' · oggi non c’è spazio' : ` · oggi dalle ${clockOf(start)}`
        }`;
      }
      case 'task':
        return [
          item.title,
          item.dueDate ? formatShortDay(item.dueDate) : 'senza scadenza',
          item.priority > 0 ? `priorità ${PRIORITY_LABELS[item.priority]}` : null,
        ]
          .filter(Boolean)
          .join(' · ');
    }
  };

  const results = useMemo<Result[]>(() => {
    const query = searchKey(text.trim());
    if (query.length < 2) return [];
    const found: Result[] = [];
    for (const task of tasks) {
      if (!searchKey(task.title).includes(query)) continue;
      found.push({
        id: task.id,
        kind: task.completedAt ? 'Attività fatta' : 'Attività',
        title: task.title,
        meta: task.dueDate ? formatShortDay(task.dueDate) : '',
        go: () => void navigate({ to: '/attivita', search: { scheda: task.id } }),
      });
    }
    for (const event of events) {
      if (!searchKey(event.title).includes(query)) continue;
      const day = toISODate(new Date(event.startsAt));
      found.push({
        id: event.id,
        kind: 'Evento',
        title: event.title,
        meta: formatShortDay(day),
        go: () => void navigate({ to: '/calendario', search: { giorno: day } }),
      });
    }
    for (const transaction of money.transactions) {
      const label = money.categoryLabel(transaction.categoryId) ?? '';
      if (!searchKey(`${transaction.note} ${label}`).includes(query)) continue;
      found.push({
        id: transaction.id,
        kind: 'Movimento',
        title: transaction.note || label || 'Movimento',
        meta: `${formatCents(transaction.amount, { signed: true })} · ${formatShortDay(transaction.occurredOn)}`,
        go: () =>
          void navigate({
            to: '/calendario',
            search: { giorno: transaction.occurredOn, vista: 'giorno' },
          }),
      });
    }
    return found.slice(0, 8);
  }, [text, tasks, events, money, navigate]);

  function create(item: Intent) {
    setError(null);
    try {
      if (item.kind === 'transaction') {
        const accountId = account(item.method);
        if (!accountId) {
          setError('Prima crea un conto in Finanze.');
          return;
        }
        const categories = item.amount > 0 ? money.incomeCategories : money.expenseCategories;
        store.create('transactions', {
          accountId,
          categoryId: guessCategory(item.note, categories),
          amount: item.amount,
          occurredOn: item.day,
          note: item.note,
          method: item.method,
          time: item.day === today ? clockNow() : null,
        });
      } else if (item.kind === 'event') {
        const startsAt = joinInstant(item.day, item.time);
        store.create('events', {
          title: item.title,
          notes: '',
          allDay: false,
          startsAt,
          endsAt: new Date(Date.parse(startsAt) + item.minutes * 60_000).toISOString(),
          taskId: null,
        });
      } else if (item.kind === 'block') {
        const start = blockStart(item.minutes);
        if (start === null) {
          setError(`Oggi non c’è uno spazio libero di ${duration(item.minutes)}.`);
          return;
        }
        // A card with the same name gets the block: its time counts there.
        const card = tasks.find(
          (task) => !task.completedAt && searchKey(task.title) === searchKey(item.title),
        );
        const startsAt = joinInstant(today, clockOf(start));
        store.create('events', {
          title: item.title,
          notes: '',
          allDay: false,
          startsAt,
          endsAt: new Date(Date.parse(startsAt) + item.minutes * 60_000).toISOString(),
          taskId: card?.id ?? null,
        });
      } else {
        store.create('tasks', {
          listId: null,
          title: item.title,
          notes: '',
          dueDate: item.dueDate,
          priority: item.priority,
          completedAt: null,
          position: nextPosition(tasks),
        });
      }
      onDone();
    } catch {
      setError('Non aggiunto: controlla quello che hai scritto.');
    }
  }

  // Row 0 is "add", the others are the results.
  const rows = (intent ? 1 : 0) + results.length;
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab' && intents.length > 1) {
      event.preventDefault();
      setReading((value) => value + (event.shiftKey ? intents.length - 1 : 1));
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected((value) => Math.min(rows - 1, value + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected((value) => Math.max(0, value - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const index = intent ? selected - 1 : selected;
      if (intent && selected === 0) create(intent);
      else if (results[index]) {
        results[index].go();
        onDone();
      }
    }
  };

  return (
    <div className={styles.palette}>
      <input
        ref={input}
        data-autofocus
        className={styles.input}
        placeholder="pizza 12 contanti ieri · dentista domani 15 · studio 2 ore · cerca…"
        value={text}
        aria-label="Aggiungi o cerca"
        onChange={(event) => {
          setText(event.target.value);
          setSelected(0);
          setReading(0);
          setError(null);
        }}
        onKeyDown={onKeyDown}
      />
      {intent ? (
        <button
          type="button"
          className={cx(styles.row, selected === 0 && styles.rowOn)}
          onClick={() => create(intent)}
        >
          <span className={styles.kind}>{KIND_LABEL[intent.kind]}</span>
          <span className={styles.rowText}>{describe(intent)}</span>
          <Kbd>Invio</Kbd>
        </button>
      ) : null}
      {intents.length > 1 ? (
        <p className={styles.hint}>
          <Kbd>Tab</Kbd> per leggerlo come{' '}
          {intents
            .filter((item) => item !== intent)
            .map((item) => KIND_LABEL[item.kind].toLowerCase())
            .join(' o ')}
        </p>
      ) : null}
      {error ? <p className={styles.error}>{error}</p> : null}
      {results.length > 0 ? (
        <ul className={styles.results} aria-label="Risultati">
          {results.map((result, index) => {
            const row = intent ? index + 1 : index;
            return (
              <li key={`${result.kind}-${result.id}`}>
                <button
                  type="button"
                  className={cx(styles.row, selected === row && styles.rowOn)}
                  onClick={() => {
                    result.go();
                    onDone();
                  }}
                >
                  <span className={styles.kind}>{result.kind}</span>
                  <span className={styles.rowText}>{result.title}</span>
                  <span className={styles.meta}>{result.meta}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {!text ? (
        <p className={styles.hint}>
          Scrivi come parli: un importo diventa un movimento, un giorno con un’ora un evento, “2
          ore” un blocco di tempo oggi, il resto un’attività. Oppure cerca tra schede, eventi e
          movimenti.
        </p>
      ) : null}
    </div>
  );
}
